import React, { useState, useEffect } from "react";
import { RiDeleteBin5Line } from "react-icons/ri";
import { ChevronUp, ChevronDown } from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BaseUrl } from "../App";
import { Circles } from 'react-loader-spinner';
import { FadeLoader } from 'react-spinners';

// Enhanced Toggle component with better styling
const Toggle = ({ label, isOn, onToggle }) => (
  <div className="flex items-center justify-between">
    <span className="text-gray-700 text-sm font-medium">{label}</span>
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        value=""
        className="sr-only peer"
        checked={isOn}
        onChange={onToggle}
      />
      <div className="group peer bg-gray-200 rounded-full duration-300 w-12 h-6 after:duration-300 after:bg-white after:shadow-md peer-checked:after:bg-white peer-checked:bg-green-500 after:rounded-full after:absolute after:h-5 after:w-5 after:top-0.5 after:left-0.5 peer-checked:after:translate-x-6 peer-hover:after:scale-95"></div>
    </label>
  </div>
);

const Recurrence = () => {
  const [recurrences, setRecurrences] = useState([]);
  const [isEnabled, setIsEnabled] = useState(() => {
    const saved = sessionStorage.getItem("isEnabled");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [isSliderOpen, setIsSliderOpen] = useState(false);
  const [newRecurrence, setNewRecurrence] = useState({
    name: "",
    label: "",
    days: "",
    discountType: "",
    discountValue: "",
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [recurrenceData, setRecurrenceData] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState("");
  const [enabledDiscounts, setEnabledDiscounts] = useState({});
  const [defaultDiscount, setDefaultDiscount] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let timer;
    if (error) {
      timer = setTimeout(() => {
        setError("");
      }, 5000); // 5 seconds
    }
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    fetchRecurrences();
  }, []);

  useEffect(() => {
    sessionStorage.setItem("isEnabled", JSON.stringify(isEnabled));
  }, [isEnabled]);

  const fetchRecurrences = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${BaseUrl}/backend/api/frequently_discount.php`,
        {
          action: "get_all_discounts",
          api_key: "1",
        }
      );

      if (response.data.status === "true") {
        setRecurrences(
          response.data.response.map((recurrence) => ({
            ...recurrence,
            expanded: false,
            isEnabled: recurrence.status === "E",
          }))
        );
      } else {
        toast.error("Failed to fetch recurrences.");
      }
    } catch (error) {
      console.error("Error fetching recurrences:", error);
      toast.error("Failed to fetch recurrences.");
    } finally {
      setLoading(false);
    }
  };

  const addRecurrenceData = async (newRecurrence) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${BaseUrl}/backend/api/frequently_discount.php`,
        {
          action: "add_discount",
          api_key: "1",
          discount_typename: newRecurrence.name,
          days: newRecurrence.days,
          d_type: newRecurrence.discountType === "percentage" ? "P" : "F",
          rates: newRecurrence.discountValue,
          label: newRecurrence.label,
          booking_count: "5",
          stripe_plan_id: "plan_12345",
        }
      );

      if (response.data.status === "true") {
        setSuccessMessage("Add Frequently Discount Successfully!");

        const processedData = {
          ...response.data,
          enabled: true,
        };

        setRecurrenceData((prevData) => ({
          ...prevData,
          [newRecurrence.discountType]: processedData,
        }));

        setIsSliderOpen(false);
        setNewRecurrence({
          name: "",
          label: "",
          days: "",
          discountType: "",
          discountValue: "",
        });

        toast.success("Recurrence added successfully");
        fetchRecurrences();
      } else {
        console.error("Failed to add recurrence:", response.data.response);
        toast.error("Failed to add recurrence. Please try again.");
      }
    } catch (error) {
      console.error("Error adding recurrence:", error);
      toast.error("Failed to add recurrence. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewRecurrence((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const toggleExpand = (id) => {
    setRecurrences(
      recurrences.map((recurrence) =>
        recurrence.id === id
          ? { ...recurrence, expanded: !recurrence.expanded }
          : { ...recurrence, expanded: false }
      )
    );
  };

  const handleEditChange = (id, field, value) => {
    setRecurrences(
      recurrences.map((recurrence) =>
        recurrence.id === id ? { ...recurrence, [field]: value } : recurrence
      )
    );
  };

  const updateRecurrence = async (id) => {
    setLoading(true);
    const recurrenceToUpdate = recurrences.find((r) => r.id === id);
    try {
      let response;
      if (recurrenceToUpdate.id === "1") {
        response = await axios.post(
          `${BaseUrl}/backend/api/frequently_discount.php`,
          {
            action: "update_discount_freq_once",
            api_key: "1",
            id: id,
            discount_typename: recurrenceToUpdate.discount_typename,
            label: recurrenceToUpdate.labels,
          }
        );
      } else {
        response = await axios.post(
          `${BaseUrl}/backend/api/frequently_discount.php`,
          {
            action: "update_discount_freq",
            api_key: "1",
            id: id,
            discount_typename: recurrenceToUpdate.discount_typename,
            d_type: recurrenceToUpdate.d_type,
            days: recurrenceToUpdate.days,
            rates: recurrenceToUpdate.rates,
            label: recurrenceToUpdate.labels,
            booking_count: recurrenceToUpdate.booking_count || "5",
          }
        );
      }

      if (response.data.status === "true") {
        setSuccessMessage("Recurrence updated successfully");
        toast.success("Recurrence updated successfully");
        fetchRecurrences();
      } else {
        toast.error("Failed to update recurrence");
      }
    } catch (error) {
      console.error("Error updating recurrence:", error);
      toast.error("Failed to update recurrence");
    } finally {
      setLoading(false);
    }
  };

  const deleteRecurrence = async (id) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${BaseUrl}/backend/api/frequently_discount.php`,
        {
          action: "delete_discount_freq",
          api_key: "1",
          id: id,
        }
      );

      if (response.data.status === "true") {
        setSuccessMessage("Recurrence deleted successfully");
        toast.success("Recurrence deleted successfully");
        setRecurrences(recurrences.filter((r) => r.id !== id));
      } else {
        toast.error("Failed to delete recurrence");
      }
    } catch (error) {
      console.error("Error deleting recurrence:", error);
      toast.error("Failed to delete recurrence");
    } finally {
      setLoading(false);
    }
    setDeleteConfirmation(false);
  };

  const updateDiscountStatus = async (id, newStatus) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${BaseUrl}/backend/api/frequently_discount.php`,
        {
          action: "update_discount_status",
          api_key: "1",
          id: id,
          status: newStatus ? "E" : "D",
        }
      );

      if (response.data.status === "true") {
        setSuccessMessage("Discount status updated successfully");
        toast.success("Discount status updated successfully");
        setRecurrences(
          recurrences.map((recurrence) =>
            recurrence.id === id
              ? { ...recurrence, isEnabled: newStatus }
              : recurrence
          )
        );
      } else {
        toast.error("Failed to update discount status");
      }
    } catch (error) {
      console.error("Error updating discount status:", error);
      toast.error("Failed to update discount status");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen">
      {/* Loading Indicator */}
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="flex flex-col items-center p-3 bg-black border border-white rounded-2xl">
            <FadeLoader 
              height={15}
              width={5}
              color="#ffffff"
              ariaLabel="loading"
            />
            <h1 className="mt-1 text-lg text-white">Please wait...</h1>
          </div>
        </div>
      )}
         
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
            <span className="block sm:inline">{successMessage}</span>
          </div>
        )}

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        <div className="bg-white shadow-xl rounded-xl max-w-5xl mx-auto overflow-hidden">
          <div className="px-6 py-8 md:px-10">
            {/* Header section with improved spacing and styling */}
            <div className="flex justify-between items-center mb-8 border-b border-gray-100 pb-5">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Recurrence Booking</h1>
              </div>
              
              <Toggle
                label=""
                isOn={isEnabled}
                onToggle={() => setIsEnabled(!isEnabled)}
              />
            </div>

            {isEnabled && (
              <>
                {/* Add button with improved styling */}
                <button
                  className="flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg"
                  onClick={() => setIsSliderOpen(true)}
                >
                  <span className="mr-2">+</span> Add Recurrence
                </button>

                {/* Slide-in panel for adding recurrence */}
                {isSliderOpen && (
                  <div className="fixed inset-0 bg-gray-500/50 z-50 flex items-center justify-center">
                    <div className="fixed top-0 right-0 w-full sm:w-2/3 md:max-w-lg h-full bg-white shadow-2xl z-50 overflow-y-auto">
                      <div className="p-6 md:p-8">
                        <div className="flex items-center justify-between mb-6 border-b border-gray-200 pb-4">
                          <h2 className="text-xl font-bold text-gray-900">
                            Add Recurrence
                          </h2>
                          <button
                            className="text-gray-500 hover:text-gray-700 text-2xl transition-colors focus:outline-none"
                            onClick={() => setIsSliderOpen(false)}
                          >
                            ×
                          </button>
                        </div>

                        <div className="space-y-5">
                          <div>
                            <label className="block text-gray-700 font-medium mb-2">
                              Recurrence Name
                            </label>
                            <input
                              type="text"
                              name="name"
                              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                              value={newRecurrence.name}
                              onChange={handleInputChange}
                              placeholder="Enter recurrence name"
                            />
                          </div>

                          <div>
                            <label className="block text-gray-700 font-medium mb-2">
                              Recurrence Label
                            </label>
                            <input
                              type="text"
                              name="label"
                              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                              value={newRecurrence.label} // Fixed: Changed from labels to label
                              onChange={handleInputChange}
                              placeholder="Enter label"
                            />
                          </div>

                          <div>
                            <label className="block text-gray-700 font-medium mb-2">
                              Recurrence Days
                            </label>
                            <input
                              type="number"
                              name="days"
                              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                              value={newRecurrence.days}
                              onChange={handleInputChange}
                              placeholder="Enter days"
                            />
                          </div>

                          <div>
                            <label className="block text-gray-700 font-medium mb-2">
                              Discount Type
                            </label>
                            <select
                              name="discountType"
                              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition bg-white"
                              value={newRecurrence.discountType}
                              onChange={handleInputChange}
                            >
                              <option value="">Select Discount Type</option>
                              <option value="percentage">Percentage</option>
                              <option value="flat">Flat</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-gray-700 font-medium mb-2">
                              Discount Value
                            </label>
                            <input
                              type="number"
                              name="discountValue"
                              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                              value={newRecurrence.discountValue}
                              onChange={handleInputChange}
                              placeholder="Enter value"
                            />
                          </div>

                          <div className="mt-8 flex justify-end space-x-3">
                            <button
                              className="px-5 py-2.5 rounded-lg bg-gray-200 text-gray-800 font-medium hover:bg-gray-300 transition-all"
                              onClick={() => setIsSliderOpen(false)}
                            >
                              Cancel
                            </button>
                            <button
                              className="px-5 py-2.5 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 transition-all shadow-md"
                              onClick={() => addRecurrenceData(newRecurrence)}
                            >
                              Add Recurrence
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Display Recurrences with improved card styling */}
                <div className="mt-8">
                  {recurrences.length > 0 ? (
                    <ul className="space-y-4 mt-4">
                      {recurrences.map((recurrence) => (
                        <li key={recurrence.id} className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-center p-5">
                            <div className="flex items-center">
                              <span className="font-medium text-gray-800">{recurrence.discount_typename}</span>
                              {recurrence.isEnabled && (
                                <span className="ml-3 px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Active</span>
                              )}
                              {!recurrence.isEnabled && (
                                <span className="ml-3 px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Inactive</span>
                              )}
                            </div>
                            <div className="flex items-center space-x-4">
                              <Toggle
                                label=""
                                isOn={recurrence.isEnabled}
                                onToggle={() =>
                                  updateDiscountStatus(
                                    recurrence.id,
                                    !recurrence.isEnabled
                                  )
                                }
                              />
                              {/* Conditionally render the delete button */}
                              {!recurrence.isEnabled && (
                                <button
                                  className="text-red-500 hover:text-red-700 focus:outline-none p-2 rounded-full hover:bg-red-50 transition-all"
                                  onClick={() => {
                                    setDeleteId(recurrence.id);
                                    setDeleteConfirmation(true);
                                  }}
                                  aria-label="Delete recurrence"
                                >
                                  <RiDeleteBin5Line size={20} />
                                </button>
                              )}
                              <button
                                onClick={() => toggleExpand(recurrence.id)}
                                className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-200 focus:outline-none transition-all duration-200 ease-in-out"
                                aria-label={
                                  recurrence.expanded ? "Collapse" : "Expand"
                                }
                              >
                                {recurrence.expanded ? (
                                  <ChevronUp className="w-5 h-5 text-gray-600" />
                                ) : (
                                  <ChevronDown className="w-5 h-5 text-gray-600" />
                                )}
                              </button>
                            </div>
                          </div>

                          <div
                            className={`overflow-hidden transition-all duration-300 ease-in-out bg-gray-50 border-t border-gray-200`}
                            style={{
                              height: recurrence.expanded ? "auto" : "0",
                              opacity: recurrence.expanded ? "1" : "0",
                            }}
                          >
                            {recurrence.expanded && (
                              <div className="p-5 space-y-4">
                                <div>
                                  <label className="block text-gray-700 font-medium mb-2">
                                    Recurrence Name
                                  </label>
                                  <input
                                    type="text"
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                                    value={recurrence.discount_typename}
                                    onChange={(e) =>
                                      handleEditChange(
                                        recurrence.id,
                                        "discount_typename",
                                        e.target.value
                                      )
                                    }
                                  />
                                </div>
                                <div>
                                  <label className="block text-gray-700 font-medium mb-2">
                                    Recurrence Label
                                  </label>
                                  <input
                                    type="text"
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                                    value={recurrence.labels}
                                    onChange={(e) =>
                                      handleEditChange(
                                        recurrence.id,
                                        "labels",
                                        e.target.value
                                      )
                                    }
                                  />
                                </div>
                                {recurrence.id !== "1" && (
                                  <>
                                    <div>
                                      <label className="block text-gray-700 font-medium mb-2">
                                        Recurrence Days
                                      </label>
                                      <input
                                        type="text"
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                                        value={recurrence.days}
                                        onChange={(e) =>
                                          handleEditChange(
                                            recurrence.id,
                                            "days",
                                            e.target.value
                                          )
                                        }
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-gray-700 font-medium mb-2">
                                        Discount Type
                                      </label>
                                      <select
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition bg-white"
                                        value={
                                          recurrence.d_type === "P"
                                            ? "percentage"
                                            : "fixed"
                                        }
                                        onChange={(e) =>
                                          handleEditChange(
                                            recurrence.id,
                                            "d_type",
                                            e.target.value === "percentage"
                                              ? "P"
                                              : "F"
                                          )
                                        }
                                      >
                                        <option value="percentage">
                                          Percentage
                                        </option>
                                        <option value="fixed">Flat</option>
                                      </select>
                                    </div>
                                    <div>
                                      <label className="block text-gray-700 font-medium mb-2">
                                        Discount Value
                                      </label>
                                      <input
                                        type="text"
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                                        value={recurrence.rates}
                                        onChange={(e) =>
                                          handleEditChange(
                                            recurrence.id,
                                            "rates",
                                            e.target.value
                                          )
                                        }
                                      />
                                    </div>
                                  </>
                                )}
                                <button
                                  className="bg-indigo-600 text-white px-5 py-2.5 mt-4 rounded-lg font-medium hover:bg-indigo-700 transition-all shadow-md"
                                  onClick={() => updateRecurrence(recurrence.id)}
                                >
                                  Update
                                </button>
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-center py-10 bg-gray-50 rounded-lg">
                      <p className="text-gray-500">No recurrences found.</p>
                      <p className="text-sm text-gray-400 mt-1">Add your first recurrence to get started.</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Improved delete confirmation modal */}
        {deleteConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-xl shadow-2xl max-w-md w-full mx-4">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Confirm Deletion</h3>
              <p className="text-gray-600 mb-6">Are you sure you want to delete this recurrence? This action cannot be undone.</p>
              <div className="flex justify-end space-x-3">
                <button
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
                  onClick={() => setDeleteConfirmation(false)}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                  onClick={() => deleteRecurrence(deleteId)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Recurrence;




// import { RiDeleteBin5Line } from "react-icons/ri"
// import { ChevronUp, ChevronDown } from "lucide-react"
// import axios from "axios"
// import { toast } from "react-toastify"
// import "react-toastify/dist/ReactToastify.css"

// const Toggle = ({ label, isOn, onToggle }) => (
//   <div className="flex items-center justify-between">
//     <span className="text-gray-700 text-sm">{label}</span>
//     <label className="relative inline-flex items-center cursor-pointer">
//       <input type="checkbox" value="" className="sr-only peer" checked={isOn} onChange={onToggle} />
//       <div className="group peer bg-white rounded-full duration-300 w-12 h-6 ring-2 ring-red-500 after:duration-300 after:bg-red-500 peer-checked:after:bg-green-500 peer-checked:ring-green-500 after:rounded-full after:absolute after:h-4 after:w-4 after:top-1 after:left-1 peer-checked:after:translate-x-6 peer-hover:after:scale-95"></div>
//     </label>
//   </div>
// )

// const Recurrence = () => {
//   const [recurrences, setRecurrences] = useState([])
//   const [isEnabled, setIsEnabled] = useState(() => {
//     const saved = localStorage.getItem("isEnabled")
//     return saved !== null ? JSON.parse(saved) : true
//   })
//   const [isSliderOpen, setIsSliderOpen] = useState(false)
//   const [newRecurrence, setNewRecurrence] = useState({
//     name: "",
//     label: "",
//     days: "",
//     discountType: "",
//     discountValue: "",
//   })
//   const [deleteConfirmation, setDeleteConfirmation] = useState(false)
//   const [deleteId, setDeleteId] = useState(null)
//   const [recurrenceData, setRecurrenceData] = useState({})
//   const [enabledDiscounts, setEnabledDiscounts] = useState({})
//   const [defaultDiscount, setDefaultDiscount] = useState(null)

//   useEffect(() => {
//     fetchRecurrences()
//   }, [])

//   useEffect(() => {
//     localStorage.setItem("isEnabled", JSON.stringify(isEnabled))
//   }, [isEnabled])

//   const fetchRecurrences = async () => {
//     try {
//       const response = await axios.post("http://10.27.1.112:80/megaclean/backend/api/frequently_discount.php", {
//         action: "get_all_discounts",
//         api_key: "1",
//       })

//       if (response.data.status === "true") {
//         setRecurrences(
//           response.data.response.map((recurrence) => ({
//             ...recurrence,
//             expanded: false,
//             isEnabled: recurrence.status === "E",
//           })),
//         )
//       } else {
//         toast.error("Failed to fetch recurrences.")
//       }
//     } catch (error) {
//       console.error("Error fetching recurrences:", error)
//       toast.error("Failed to fetch recurrences.")
//     }
//   }

//   const addRecurrenceData = async (newRecurrence) => {
//     try {
//       const response = await axios.post("http://10.27.1.112:80/megaclean/backend/api/frequently_discount.php", {
//         action: "add_discount",
//         api_key: "1",
//         discount_typename: newRecurrence.name,
//         days: newRecurrence.days,
//         d_type: newRecurrence.discountType === "percentage" ? "P" : "F",
//         rates: newRecurrence.discountValue,
//         label: newRecurrence.label,
//         booking_count: "5",
//         stripe_plan_id: "plan_12345",
//       })

//       if (response.data.status === "true") {
//         const processedData = {
//           ...response.data,
//           enabled: true,
//         }

//         setRecurrenceData((prevData) => ({
//           ...prevData,
//           [newRecurrence.discountType]: processedData,
//         }))

//         setIsSliderOpen(false)
//         setNewRecurrence({
//           name: "",
//           label: "",
//           days: "",
//           discountType: "",
//           discountValue: "",
//         })

//         toast.success("Recurrence added successfully")
//         fetchRecurrences()
//       } else {
//         console.error("Failed to add recurrence:", response.data.response)
//         toast.error("Failed to add recurrence. Please try again.")
//       }
//     } catch (error) {
//       console.error("Error adding recurrence:", error)
//       toast.error("Failed to add recurrence. Please try again.")
//     }
//   }

//   const handleInputChange = (e) => {
//     const { name, value } = e.target
//     setNewRecurrence((prev) => ({
//       ...prev,
//       [name]: value,
//     }))
//   }

//   const toggleExpand = (id) => {
//     setRecurrences(
//       recurrences.map((recurrence) =>
//         recurrence.id === id ? { ...recurrence, expanded: !recurrence.expanded } : { ...recurrence, expanded: false },
//       ),
//     )
//   }

//   const handleEditChange = (id, field, value) => {
//     setRecurrences(
//       recurrences.map((recurrence) => (recurrence.id === id ? { ...recurrence, [field]: value } : recurrence)),
//     )
//   }

//   const updateRecurrence = async (id) => {
//     const recurrenceToUpdate = recurrences.find((r) => r.id === id)
//     try {
//       let response
//       if (recurrenceToUpdate.id === "1") {
//         // Use update_discount_freq_once for the first recurrence
//         response = await axios.post("http://localhost/megaclean/backend/api/frequently_discount.php", {
//           action: "update_discount_freq_once",
//           api_key: "1",
//           id: id,
//           discount_typename: recurrenceToUpdate.discount_typename,
//           label: recurrenceToUpdate.labels,
//         })
//       } else {
//         // Use update_discount_freq for other recurrences
//         response = await axios.post("http://localhost/megaclean/backend/api/frequently_discount.php", {
//           action: "update_discount_freq",
//           api_key: "1",
//           id: id,
//           discount_typename: recurrenceToUpdate.discount_typename,
//           d_type: recurrenceToUpdate.d_type,
//           days: recurrenceToUpdate.days,
//           rates: recurrenceToUpdate.rates,
//           label: recurrenceToUpdate.labels,
//           booking_count: recurrenceToUpdate.booking_count || "5",
//         })
//       }

//       if (response.data.status === "true") {
//         toast.success("Recurrence updated successfully")
//         fetchRecurrences()
//       } else {
//         toast.error("Failed to update recurrence")
//       }
//     } catch (error) {
//       console.error("Error updating recurrence:", error)
//       toast.error("Failed to update recurrence")
//     }
//   }

//   const deleteRecurrence = async (id) => {
//     try {
//       const response = await axios.post("http://localhost/megaclean/backend/api/frequently_discount.php", {
//         action: "delete_discount_freq",
//         api_key: "1",
//         id: id,
//       })

//       if (response.data.status === "true") {
//         toast.success("Recurrence deleted successfully")
//         setRecurrences(recurrences.filter((r) => r.id !== id))
//       } else {
//         toast.error("Failed to delete recurrence")
//       }
//     } catch (error) {
//       console.error("Error deleting recurrence:", error)
//       toast.error("Failed to delete recurrence")
//     }
//     setDeleteConfirmation(false)
//   }

//   const updateDiscountStatus = async (id, newStatus) => {
//     try {
//       const response = await axios.post("http://localhost/megaclean/backend/api/frequently_discount.php", {
//         action: "update_discount_status",
//         api_key: "1",
//         id: id,
//         status: newStatus ? "E" : "D",
//       })

//       if (response.data.status === "true") {
//         toast.success("Discount status updated successfully")
//         setRecurrences(
//           recurrences.map((recurrence) =>
//             recurrence.id === id ? { ...recurrence, isEnabled: newStatus } : recurrence,
//           ),
//         )
//       } else {
//         toast.error("Failed to update discount status")
//       }
//     } catch (error) {
//       console.error("Error updating discount status:", error)
//       toast.error("Failed to update discount status")
//     }
//   }

//   return (
//     <div className="min-h-screen bg-white p-6 relative xl:px-52">
//       <div className="bg-white shadow-lg rounded-lg p-6 container mx-auto px-4 md:px-6 lg:px-8 xl:px-24 py-6">
//         <div className="justify-between items-center mb-4 "></div>
//         <Toggle label="" isOn={isEnabled} onToggle={() => setIsEnabled(!isEnabled)} />

//         {isEnabled && (
//           <>
//             <button
//               className="bg-green-500 text-white px-4 py-2 mb-4 rounded hover:bg-green-600"
//               onClick={() => setIsSliderOpen(true)}
//             >
//               + Add Recurrence
//             </button>

//             {isSliderOpen && (
//               <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
//                 <div className="fixed top-0 right-0 w-full sm:w-1/3 h-full bg-white shadow-xl z-50 overflow-y-auto">
//                   <div className="p-6">
//                     <div className="flex items-center justify-between mb-6 border-b pb-4">
//                       <h2 className="text-xl font-semibold text-gray-900">Add Recurrence</h2>
//                       <button
//                         className="text-gray-500 hover:text-gray-700 transition-colors"
//                         onClick={() => setIsSliderOpen(false)}
//                       >
//                         ×
//                       </button>
//                     </div>

//                     <div className="space-y-4">
//                       <div>
//                         <label className="block text-gray-700 font-medium mb-2">Recurrence Name</label>
//                         <input
//                           type="text"
//                           name="name"
//                           className="w-full border rounded-lg px-3 py-2"
//                           value={newRecurrence.name}
//                           onChange={handleInputChange}
//                         />
//                       </div>

//                       <div>
//                         <label className="block text-gray-700 font-medium mb-2">Recurrence Label</label>
//                         <input
//                           type="text"
//                           name="label"
//                           className="w-full border rounded-lg px-3 py-2"
//                           value={newRecurrence.labels}
//                           onChange={handleInputChange}
//                         />
//                       </div>

//                       <div>
//                         <label className="block text-gray-700 font-medium mb-2">Recurrence Days</label>
//                         <input
//                           type="number"
//                           name="days"
//                           className="w-full border rounded-lg px-3 py-2"
//                           value={newRecurrence.days}
//                           onChange={handleInputChange}
//                         />
//                       </div>

//                       <div>
//                         <label className="block text-gray-700 font-medium mb-2">Discount Type</label>
//                         <select
//                           name="discountType"
//                           className="w-full border rounded-lg px-3 py-2"
//                           value={newRecurrence.discountType}
//                           onChange={handleInputChange}
//                         >
//                           <option value="">Select Discount Type</option>
//                           <option value="percentage">Percentage</option>
//                           <option value="Flat">Flat</option>
//                         </select>
//                       </div>

//                       <div>
//                         <label className="block text-gray-700 font-medium mb-2">Discount Value</label>
//                         <input
//                           type="number"
//                           name="discountValue"
//                           className="w-full border rounded-lg px-3 py-2"
//                           value={newRecurrence.discountValue}
//                           onChange={handleInputChange}
//                         />
//                       </div>

//                       <div className="mt-6 flex justify-end space-x-3">
//                         <button
//                           className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
//                           onClick={() => setIsSliderOpen(false)}
//                         >
//                           Cancel
//                         </button>
//                         <button
//                           className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
//                           onClick={() => addRecurrenceData(newRecurrence)}
//                         >
//                           Add Recurrence
//                         </button>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             )}

//             {/* Display Recurrences */}
//             <div className="mt-6">
//               <h2 className="text-xl font-semibold">Recurrence Booking</h2>
//               {recurrences.length > 0 ? (
//                 <ul className="space-y-4 mt-4">
//                   {recurrences.map((recurrence) => (
//                     <li key={recurrence.id} className="border rounded-lg p-4">
//                       <div className="flex justify-between items-center">
//                         <span>{recurrence.discount_typename}</span>
//                         <div className="flex items-center space-x-4">
//                           <Toggle
//                             label=""
//                             isOn={recurrence.isEnabled}
//                             onToggle={() => updateDiscountStatus(recurrence.id, !recurrence.isEnabled)}
//                           />
//                           <button
//                             className="text-red-500 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200 ease-in-out transform hover:scale-105"
//                             onClick={() => {
//                               setDeleteId(recurrence.id)
//                               setDeleteConfirmation(true)
//                             }}
//                           >
//                             <RiDeleteBin5Line size={20} />
//                           </button>

//                           <button
//                             onClick={() => toggleExpand(recurrence.id)}
//                             className="w-10 h-10 flex items-center justify-center bg-white border border-gray-300 rounded-full shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 ease-in-out transform hover:scale-105"
//                             aria-label={recurrence.expanded ? "Collapse" : "Expand"}
//                           >
//                             {recurrence.expanded ? (
//                               <ChevronUp className="w-5 h-5 text-gray-600" />
//                             ) : (
//                               <ChevronDown className="w-5 h-5 text-gray-600" />
//                             )}
//                           </button>
//                         </div>
//                       </div>

//                       <div
//                         className={`overflow-hidden transition-all duration-1000 ease-in-out`}
//                         style={{
//                           height: recurrence.expanded ? "auto" : "0",
//                           opacity: recurrence.expanded ? "1" : "0",
//                           paddingTop: recurrence.expanded ? "10px" : "0",
//                           paddingBottom: recurrence.expanded ? "10px" : "0",
//                         }}
//                       >
//                         {recurrence.expanded && (
//                           <div className="p-4 bg-white space-y-4">
//                             <div>
//                               <label className="block text-gray-700 font-medium">Recurrence Name</label>
//                               <input
//                                 type="text"
//                                 className="w-full border rounded px-3 py-2"
//                                 value={recurrence.discount_typename}
//                                 onChange={(e) => handleEditChange(recurrence.id, "discount_typename", e.target.value)}
//                               />
//                             </div>
//                             <div>
//                               <label className="block text-gray-700 font-medium">Recurrence Label</label>
//                               <input
//                                 type="text"
//                                 className="w-full border rounded px-3 py-2"
//                                 value={recurrence.labels}
//                                 onChange={(e) => handleEditChange(recurrence.id, "labels", e.target.value)}
//                               />
//                             </div>
//                             {recurrence.id !== "1" && (
//                               <>
//                                 <div>
//                                   <label className="block text-gray-700 font-medium">Recurrence Days</label>
//                                   <input
//                                     type="text"
//                                     className="w-full border rounded px-3 py-2"
//                                     value={recurrence.days}
//                                     onChange={(e) => handleEditChange(recurrence.id, "days", e.target.value)}
//                                   />
//                                 </div>
//                                 <div>
//                                   <label className="block text-gray-700 font-medium">Discount Type</label>
//                                   <select
//                                     className="w-full border rounded px-3 py-2"
//                                     value={recurrence.d_type === "P" ? "percentage" : "fixed"}
//                                     onChange={(e) =>
//                                       handleEditChange(
//                                         recurrence.id,
//                                         "d_type",
//                                         e.target.value === "percentage" ? "P" : "F",
//                                       )
//                                     }
//                                   >
//                                     <option value="percentage">Percentage</option>
//                                     <option value="fixed">Flat</option>
//                                   </select>
//                                 </div>
//                                 <div>
//                                   <label className="block text-gray-700 font-medium">Discount Value</label>
//                                   <input
//                                     type="text"
//                                     className="w-full border rounded px-3 py-2"
//                                     value={recurrence.rates}
//                                     onChange={(e) => handleEditChange(recurrence.id, "rates", e.target.value)}
//                                   />
//                                 </div>
//                               </>
//                             )}
//                             <button
//                               className="bg-blue-500 text-white px-4 py-2 mt-4 rounded hover:bg-blue-600"
//                               onClick={() => updateRecurrence(recurrence.id)}
//                             >
//                               Update
//                             </button>
//                           </div>
//                         )}
//                       </div>
//                     </li>
//                   ))}
//                 </ul>
//               ) : (
//                 <p>No recurrences found.</p>
//               )}
//             </div>
//           </>
//         )}
//       </div>
//       {deleteConfirmation && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
//           <div className="bg-white p-6 rounded-lg">
//             <p>Are you sure you want to delete this recurrence?</p>
//             <div className="mt-4 flex justify-end space-x-2">
//               <button className="px-4 py-2 bg-gray-200 rounded" onClick={() => setDeleteConfirmation(false)}>
//                 Cancel
//               </button>
//               <button className="px-4 py-2 bg-red-500 text-white rounded" onClick={() => deleteRecurrence(deleteId)}>
//                 Delete
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   )
// }

// export default Recurrence

// import React, { useState, useEffect } from "react";
// import { RiDeleteBin5Line } from "react-icons/ri";
// import { ChevronUp, ChevronDown } from 'lucide-react';
// import axios from 'axios';
// import { toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';

// const Toggle = ({ label, isOn, onToggle }) => (
//     <div className="flex items-center justify-between">
//         <span className="text-gray-700 text-sm">{label}</span>
//         <label className="relative inline-flex items-center cursor-pointer">
//             <input
//                 type="checkbox"
//                 value=""
//                 className="sr-only peer"
//                 checked={isOn}
//                 onChange={onToggle}
//             />
//             <div className="group peer bg-white rounded-full duration-300 w-12 h-6 ring-2 ring-red-500 after:duration-300 after:bg-red-500 peer-checked:after:bg-green-500 peer-checked:ring-green-500 after:rounded-full after:absolute after:h-4 after:w-4 after:top-1 after:left-1 peer-checked:after:translate-x-6 peer-hover:after:scale-95"></div>
//         </label>
//     </div>
// );

// const Recurrence = () => {
//     const [recurrences, setRecurrences] = useState([]);
//     const [isEnabled, setIsEnabled] = useState(false);
//     const [isSliderOpen, setIsSliderOpen] = useState(false);
//     const [newRecurrence, setNewRecurrence] = useState({
//         name: "",
//         label: "",
//         days: "",
//         discountType: "",
//         discountValue: "",
//     });
//     const [deleteConfirmation, setDeleteConfirmation] = useState(false);
//     const [deleteId, setDeleteId] = useState(null);
//     const [recurrenceData, setRecurrenceData] = useState({});
//     const [enabledDiscounts, setEnabledDiscounts] = useState({});
//     const [defaultDiscount, setDefaultDiscount] = useState(null);

//     useEffect(() => {
//         fetchRecurrences();
//     }, []);

//     const fetchRecurrences = async () => {
//         try {
//             const response = await axios.post('http://localhost/megaclean/backend/api/frequently_discount.php', {
//                 action: 'get_all_discounts',
//                 api_key: '1'
//             });

//             if (response.data.status === 'true') {
//                 setRecurrences(response.data.response.map(recurrence => ({
//                     ...recurrence,
//                     expanded: false,
//                     isEnabled: recurrence.status === 'E'
//                 })));
//             } else {
//                 toast.error('Failed to fetch recurrences.');
//             }
//         } catch (error) {
//             console.error('Error fetching recurrences:', error);
//             toast.error('Failed to fetch recurrences.');
//         }
//     };

//     const addRecurrenceData = async (newRecurrence) => {
//         try {
//             const response = await axios.post('http://localhost/megaclean/backend/api/frequently_discount.php', {
//                 action: 'add_discount',
//                 api_key: '1',
//                 discount_typename: newRecurrence.name,
//                 days: newRecurrence.days,
//                 d_type: newRecurrence.discountType === 'percentage' ? 'P' : 'F',
//                 rates: newRecurrence.discountValue,
//                 label: newRecurrence.label,
//                 booking_count: "5",
//                 stripe_plan_id: "plan_12345"
//             });

//             if (response.data.status === 'true') {
//                 const processedData = {
//                     ...response.data,
//                     enabled: true
//                 };

//                 setRecurrenceData(prevData => ({
//                     ...prevData,
//                     [newRecurrence.discountType]: processedData
//                 }));

//                 setIsSliderOpen(false);
//                 setNewRecurrence({
//                     name: "",
//                     label: "",
//                     days: "",
//                     discountType: "",
//                     discountValue: "",
//                 });

//                 toast.success('Recurrence added successfully');
//                 fetchRecurrences();
//             } else {
//                 console.error('Failed to add recurrence:', response.data.response);
//                 toast.error('Failed to add recurrence. Please try again.');
//             }
//         } catch (error) {
//             console.error('Error adding recurrence:', error);
//             toast.error('Failed to add recurrence. Please try again.');
//         }
//     };

//     const handleInputChange = (e) => {
//         const { name, value } = e.target;
//         setNewRecurrence(prev => ({
//             ...prev,
//             [name]: value
//         }));
//     };

//     const toggleExpand = (id) => {
//         setRecurrences(recurrences.map(recurrence =>
//             recurrence.id === id
//                 ? { ...recurrence, expanded: !recurrence.expanded }
//                 : { ...recurrence, expanded: false }
//         ));
//     };

//     const handleEditChange = (id, field, value) => {
//         setRecurrences(recurrences.map(recurrence =>
//             recurrence.id === id ? { ...recurrence, [field]: value } : recurrence
//         ));
//     };

//     const updateRecurrence = async (id) => {
//         const recurrenceToUpdate = recurrences.find(r => r.id === id);
//         try {
//             let response;
//             if (recurrenceToUpdate.id === '1') {
//                 // Use update_discount_freq_once for the first recurrence
//                 response = await axios.post('http://localhost/megaclean/backend/api/frequently_discount.php', {
//                     action: 'update_discount_freq_once',
//                     api_key: '1',
//                     id: id,
//                     discount_typename: recurrenceToUpdate.discount_typename,
//                     label: recurrenceToUpdate.labels
//                 });
//             } else {
//                 // Use update_discount_freq for other recurrences
//                 response = await axios.post('http://localhost/megaclean/backend/api/frequently_discount.php', {
//                     action: 'update_discount_freq',
//                     api_key: '1',
//                     id: id,
//                     discount_typename: recurrenceToUpdate.discount_typename,
//                     d_type: recurrenceToUpdate.d_type,
//                     days: recurrenceToUpdate.days,
//                     rates: recurrenceToUpdate.rates,
//                     label: recurrenceToUpdate.labels,
//                     booking_count: recurrenceToUpdate.booking_count || "5"
//                 });
//             }

//             if (response.data.status === 'true') {
//                 toast.success('Recurrence updated successfully');
//                 fetchRecurrences();
//             } else {
//                 toast.error('Failed to update recurrence');
//             }
//         } catch (error) {
//             console.error('Error updating recurrence:', error);
//             toast.error('Failed to update recurrence');
//         }
//     };

//     const deleteRecurrence = async (id) => {
//         try {
//             const response = await axios.post('http://localhost/megaclean/backend/api/frequently_discount.php', {
//                 action: 'delete_discount_freq',
//                 api_key: '1',
//                 id: id
//             });

//             if (response.data.status === 'true') {
//                 toast.success('Recurrence deleted successfully');
//                 setRecurrences(recurrences.filter(r => r.id !== id));
//             } else {
//                 toast.error('Failed to delete recurrence');
//             }
//         } catch (error) {
//             console.error('Error deleting recurrence:', error);
//             toast.error('Failed to delete recurrence');
//         }
//         setDeleteConfirmation(false);
//     };

//     const updateDiscountStatus = async (id, newStatus) => {
//         try {
//             const response = await axios.post('http://localhost/megaclean/backend/api/frequently_discount.php', {
//                 action: 'update_discount_status',
//                 api_key: '1',
//                 id: id,
//                 status: newStatus ? 'E' : 'D'
//             });

//             if (response.data.status === 'true') {
//                 toast.success('Discount status updated successfully');
//                 setRecurrences(recurrences.map(recurrence =>
//                     recurrence.id === id ? { ...recurrence, isEnabled: newStatus } : recurrence
//                 ));
//             } else {
//                 toast.error('Failed to update discount status');
//             }
//         } catch (error) {
//             console.error('Error updating discount status:', error);
//             toast.error('Failed to update discount status');
//         }
//     };

//     return (
//         <div className="min-h-screen bg-white p-6 relative xl:px-52">
//             <div className="bg-white shadow-lg rounded-lg p-6 container mx-auto px-4 md:px-6 lg:px-8 xl:px-24 py-6">
//                 <div className="justify-between items-center mb-4 ">
//                 </div>
//                 <Toggle
//                     label=""
//                     isOn={isEnabled}
//                     onToggle={() => setIsEnabled(!isEnabled)}
//                 />

//                 {isEnabled && (
//                     <>
//                         <button
//                             className="bg-green-500 text-white px-4 py-2 mb-4 rounded hover:bg-green-600"
//                             onClick={() => setIsSliderOpen(true)}
//                         >
//                             + Add Recurrence
//                         </button>

//                         {isSliderOpen && (
//                             <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
//                                 <div className="fixed top-0 right-0 w-full sm:w-1/3 h-full bg-white shadow-xl z-50 overflow-y-auto">
//                                     <div className="p-6">
//                                         <div className="flex items-center justify-between mb-6 border-b pb-4">
//                                             <h2 className="text-xl font-semibold text-gray-900">Add Recurrence</h2>
//                                             <button
//                                                 className="text-gray-500 hover:text-gray-700 transition-colors"
//                                                 onClick={() => setIsSliderOpen(false)}
//                                             >
//                                                 ×
//                                             </button>
//                                         </div>

//                                         <div className="space-y-4">
//                                             <div>
//                                                 <label className="block text-gray-700 font-medium mb-2">
//                                                     Recurrence Name
//                                                 </label>
//                                                 <input
//                                                     type="text"
//                                                     name="name"
//                                                     className="w-full border rounded-lg px-3 py-2"
//                                                     value={newRecurrence.name}
//                                                     onChange={handleInputChange}
//                                                 />
//                                             </div>

//                                             <div>
//                                                 <label className="block text-gray-700 font-medium mb-2">
//                                                     Recurrence Label
//                                                 </label>
//                                                 <input
//                                                     type="text"
//                                                     name="label"
//                                                     className="w-full border rounded-lg px-3 py-2"
//                                                     value={newRecurrence.labels}
//                                                     onChange={handleInputChange}
//                                                 />
//                                             </div>

//                                             <div>
//                                                 <label className="block text-gray-700 font-medium mb-2">
//                                                     Recurrence Days
//                                                 </label>
//                                                 <input
//                                                     type="number"
//                                                     name="days"
//                                                     className="w-full border rounded-lg px-3 py-2"
//                                                     value={newRecurrence.days}
//                                                     onChange={handleInputChange}
//                                                 />
//                                             </div>

//                                             <div>
//                                                 <label className="block text-gray-700 font-medium mb-2">
//                                                     Discount Type
//                                                 </label>
//                                                 <select
//                                                     name="discountType"
//                                                     className="w-full border rounded-lg px-3 py-2"
//                                                     value={newRecurrence.discountType}
//                                                     onChange={handleInputChange}
//                                                 >
//                                                     <option value="">Select Discount Type</option>
//                                                     <option value="percentage">Percentage</option>
//                                                     <option value="Flat">Flat</option>
//                                                 </select>
//                                             </div>

//                                             <div>
//                                                 <label className="block text-gray-700 font-medium mb-2">
//                                                     Discount Value
//                                                 </label>
//                                                 <input
//                                                     type="number"
//                                                     name="discountValue"
//                                                     className="w-full border rounded-lg px-3 py-2"
//                                                     value={newRecurrence.discountValue}
//                                                     onChange={handleInputChange}
//                                                 />
//                                             </div>

//                                             <div className="mt-6 flex justify-end space-x-3">
//                                                 <button
//                                                     className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
//                                                     onClick={() => setIsSliderOpen(false)}
//                                                 >
//                                                     Cancel
//                                                 </button>
//                                                 <button
//                                                     className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
//                                                     onClick={() => addRecurrenceData(newRecurrence)}
//                                                 >
//                                                     Add Recurrence
//                                                 </button>
//                                             </div>
//                                         </div>
//                                     </div>
//                                 </div>
//                             </div>
//                         )}

//                         {/* Display Recurrences */}
//                         <div className="mt-6">
//                             <h2 className="text-xl font-semibold">Recurrence Booking</h2>
//                             {recurrences.length > 0 ? (
//                                 <ul className="space-y-4 mt-4">
//                                     {recurrences.map((recurrence) => (
//                                         <li key={recurrence.id} className="border rounded-lg p-4">
//                                             <div className="flex justify-between items-center">
//                                                 <span>{recurrence.discount_typename}</span>
//                                                 <div className="flex items-center space-x-4">
//                                                     <Toggle
//                                                         label=""
//                                                         isOn={recurrence.isEnabled}
//                                                         onToggle={() => updateDiscountStatus(recurrence.id, !recurrence.isEnabled)}
//                                                     />
//                                                     <button
//                                                         className="text-red-500 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200 ease-in-out transform hover:scale-105"
//                                                         onClick={() => {
//                                                             setDeleteId(recurrence.id);
//                                                             setDeleteConfirmation(true);
//                                                         }}
//                                                     >
//                                                         <RiDeleteBin5Line size={20} />
//                                                     </button>

//                                                     <button
//                                                         onClick={() => toggleExpand(recurrence.id)}
//                                                         className="w-10 h-10 flex items-center justify-center bg-white border border-gray-300 rounded-full shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 ease-in-out transform hover:scale-105"
//                                                         aria-label={recurrence.expanded ? "Collapse" : "Expand"}
//                                                     >
//                                                         {recurrence.expanded ? (
//                                                             <ChevronUp className="w-5 h-5 text-gray-600" />
//                                                         ) : (
//                                                             <ChevronDown className="w-5 h-5 text-gray-600" />
//                                                         )}
//                                                     </button>
//                                                 </div>
//                                             </div>

//                                             <div
//                                                 className={`overflow-hidden transition-all duration-1000 ease-in-out`}
//                                                 style={{
//                                                     height: recurrence.expanded ? 'auto' : '0',
//                                                     opacity: recurrence.expanded ? '1' : '0',
//                                                     paddingTop: recurrence.expanded ? '10px' : '0',
//                                                     paddingBottom: recurrence.expanded ? '10px' : '0',
//                                                 }}
//                                             >
//                                                 {recurrence.expanded && (
//                                                     <div className="p-4 bg-white space-y-4">
//                                                         <div>
//                                                             <label className="block text-gray-700 font-medium">
//                                                                 Recurrence Name
//                                                             </label>
//                                                             <input
//                                                                 type="text"
//                                                                 className="w-full border rounded px-3 py-2"
//                                                                 value={recurrence.discount_typename}
//                                                                 onChange={(e) =>
//                                                                     handleEditChange(recurrence.id, "discount_typename", e.target.value)
//                                                                 }
//                                                             />
//                                                         </div>
//                                                         <div>
//                                                             <label className="block text-gray-700 font-medium">
//                                                                 Recurrence Label
//                                                             </label>
//                                                             <input
//                                                                 type="text"
//                                                                 className="w-full border rounded px-3 py-2"
//                                                                 value={recurrence.labels}
//                                                                 onChange={(e) =>
//                                                                     handleEditChange(recurrence.id, "labels", e.target.value)
//                                                                 }
//                                                             />
//                                                         </div>
//                                                         {recurrence.id !== '1' && (
//                                                             <>
//                                                                 <div>
//                                                                     <label className="block text-gray-700 font-medium">
//                                                                         Recurrence Days
//                                                                     </label>
//                                                                     <input
//                                                                         type="text"
//                                                                         className="w-full border rounded px-3 py-2"
//                                                                         value={recurrence.days}
//                                                                         onChange={(e) =>
//                                                                             handleEditChange(recurrence.id, "days", e.target.value)
//                                                                         }
//                                                                     />
//                                                                 </div>
//                                                                 <div>
//                                                                     <label className="block text-gray-700 font-medium">
//                                                                         Discount Type
//                                                                     </label>
//                                                                     <select
//                                                                         className="w-full border rounded px-3 py-2"
//                                                                         value={recurrence.d_type === 'P' ? 'percentage' : 'fixed'}
//                                                                         onChange={(e) =>
//                                                                             handleEditChange(recurrence.id, "d_type", e.target.value === 'percentage' ? 'P' : 'F')
//                                                                         }
//                                                                     >
//                                                                         <option value="percentage">Percentage</option>
//                                                                         <option value="fixed">Flat</option>
//                                                                     </select>
//                                                                 </div>
//                                                                 <div>
//                                                                     <label className="block text-gray-700 font-medium">
//                                                                         Discount Value
//                                                                     </label>
//                                                                     <input
//                                                                         type="text"
//                                                                         className="w-full border rounded px-3 py-2"
//                                                                         value={recurrence.rates}
//                                                                         onChange={(e) =>
//                                                                             handleEditChange(recurrence.id, "rates", e.target.value)
//                                                                         }
//                                                                     />
//                                                                 </div>
//                                                             </>
//                                                         )}
//                                                         <button
//                                                             className="bg-blue-500 text-white px-4 py-2 mt-4 rounded hover:bg-blue-600"
//                                                             onClick={() => updateRecurrence(recurrence.id)}
//                                                         >
//                                                             Update
//                                                         </button>
//                                                     </div>
//                                                 )}
//                                             </div>
//                                         </li>
//                                     ))}
//                                 </ul>
//                             ) : (
//                                 <p>No recurrences found.</p>
//                             )}
//                         </div>
//                     </>
//                 )}
//             </div>
//             {deleteConfirmation && (
//                 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
//                     <div className="bg-white p-6 rounded-lg">
//                         <p>Are you sure you want to delete this recurrence?</p>
//                         <div className="mt-4 flex justify-end space-x-2">
//                             <button
//                                 className="px-4 py-2 bg-gray-200 rounded"
//                                 onClick={() => setDeleteConfirmation(false)}
//                             >
//                                 Cancel
//                             </button>
//                             <button
//                                 className="px-4 py-2 bg-red-500 text-white rounded"
//                                 onClick={() => deleteRecurrence(deleteId)}
//                             >
//                                 Delete
//                             </button>
//                         </div>
//                     </div>
//                 </div>
//             )}
//         </div>
//     );
// };

// export default Recurrence;

// import React, { useState } from "react";
// import { RiDeleteBin5Line } from "react-icons/ri";
// import { BiSolidEdit } from "react-icons/bi";

// const Toggle = ({ label, isOn, onToggle }) => (
//     <div className="flex items-center justify-between">
//         <span className="text-gray-700 text-sm">{label}</span>
//         <label className="relative inline-flex items-center cursor-pointer">
//             <input
//                 type="checkbox"
//                 value=""
//                 className="sr-only peer"
//                 checked={isOn}
//                 onChange={onToggle}
//             />
//             <div className="group peer bg-white rounded-full duration-300 w-12 h-6 ring-2 ring-red-500 after:duration-300 after:bg-red-500 peer-checked:after:bg-green-500 peer-checked:ring-green-500 after:rounded-full after:absolute after:h-4 after:w-4 after:top-1 after:left-1 peer-checked:after:translate-x-6 peer-hover:after:scale-95"></div>
//         </label>
//     </div>
// );

// const Recurrence = () => {
//     const [recurrences, setRecurrences] = useState([
//         { id: 1, name: "Once", label: "Single Use", days: "", discountType: "", discountValue: "", enabled: false, expanded: false },
//         { id: 2, name: "Weekly", label: "Every Week", days: "7", discountType: "percentage", discountValue: "10", enabled: false, expanded: false },
//         { id: 3, name: "Bi-Weekly", label: "Every 2 Weeks", days: "14", discountType: "fixed", discountValue: "20", enabled: false, expanded: false },
//         { id: 4, name: "Monthly", label: "Every Month", days: "30", discountType: "percentage", discountValue: "15", enabled: false, expanded: false },
//     ]);

//     const [isEnabled, setIsEnabled] = useState(false); // State for main toggle
//     const [isSliderOpen, setIsSliderOpen] = useState(false); // State for slider
//     const [newRecurrence, setNewRecurrence] = useState({
//         name: "",
//         label: "",
//         days: "",
//         discountType: "",
//         discountValue: "",
//     }); // State for new recurrence form
//     const [deleteConfirmation, setDeleteConfirmation] = useState(false); // State for confirmation modal
//     const [deleteId, setDeleteId] = useState(null); // Store ID of the recurrence to delete

//     const toggleExpand = (id) => {
//         setRecurrences((prev) =>
//             prev.map((recurrence) =>
//                 recurrence.id === id
//                     ? { ...recurrence, expanded: !recurrence.expanded }
//                     : recurrence
//             )
//         );
//     };

//     const toggleEnable = (id) => {
//         setRecurrences((prev) =>
//             prev.map((recurrence) =>
//                 recurrence.id === id
//                     ? { ...recurrence, enabled: !recurrence.enabled }
//                     : recurrence
//             )
//         );
//     };

//     const handleEditChange = (id, field, value) => {
//         setRecurrences((prev) =>
//             prev.map((recurrence) =>
//                 recurrence.id === id ? { ...recurrence, [field]: value } : recurrence
//             )
//         );
//     };

//     const updateRecurrence = (id) => {
//         setRecurrences((prev) =>
//             prev.map((recurrence) =>
//                 recurrence.id === id ? { ...recurrence, expanded: false } : recurrence
//             )
//         );
//     };

//     const handleInputChange = (e) => {
//         const { name, value } = e.target;
//         setNewRecurrence((prev) => ({ ...prev, [name]: value }));
//     };

//     const addRecurrence = () => {
//         const newRecurrenceData = {
//             ...newRecurrence,
//             id: recurrences.length + 1,
//             enabled: false,
//             expanded: false,
//         };
//         setRecurrences((prev) => [...prev, newRecurrenceData]);
//         setIsSliderOpen(false); // Close slider
//         setNewRecurrence({
//             name: "",
//             label: "",
//             days: "",
//             discountType: "",
//             discountValue: "",
//         }); // Reset form
//     };

//     const deleteRecurrence = () => {
//         setRecurrences((prev) => prev.filter((recurrence) => recurrence.id !== deleteId));
//         setDeleteConfirmation(false); // Close the modal
//     };

//     const handleDeleteClick = (id) => {
//         setDeleteId(id); // Set the recurrence ID to delete
//         setDeleteConfirmation(true); // Open confirmation modal
//     };

//     return (
//         <div className="min-h-screen bg-white p-6 relative xl:px-52">
//             <div className=" bg-white shadow-lg rounded-lg p-6 container mx-auto px-4 md:px-6 lg:px-8 xl:px-24 py-6  ">
//                 <div className="flex justify-between items-center mb-4">
//                     <h1 className="text-lg font-semibold">Recurrence Booking</h1>
//                     <Toggle
//                         label=""
//                         isOn={isEnabled}
//                         onToggle={() => setIsEnabled((prev) => !prev)}
//                     />
//                 </div>
//                 <div
//                     className={`overflow-hidden transition-all mt-10 duration-1000 ease-in-out`}
//                     style={{
//                         height: isEnabled ? 'auto' : '0', // Control height for smooth transition
//                         opacity: isEnabled ? '1' : '0', // Fade in/out
//                         paddingTop: isEnabled ? '10px' : '0', // Adjust padding to smooth transition
//                         paddingBottom: isEnabled ? '10px' : '0', // Adjust padding to smooth transition
//                     }}
//                 >

//                     {isEnabled && (
//                         <>
//                             <button
//                                 className="bg-green-500 text-white px-4 py-2 mb-4 rounded hover:bg-green-600"
//                                 onClick={() => setIsSliderOpen(true)} // Open slider
//                             >
//                                 + Add Recurrence
//                             </button>

//                             {recurrences.map((recurrence) => (
//                                 <div
//                                     key={recurrence.id}
//                                     className="border-2 rounded-lg mb-4 overflow-hidden"
//                                 >
//                                     <div className="flex justify-between items-center p-4">
//                                         <span className="font-medium">{recurrence.name}</span>
//                                         <div className="flex items-center space-x-4">
//                                             <Toggle
//                                                 label=""
//                                                 isOn={recurrence.enabled}
//                                                 onToggle={() => toggleEnable(recurrence.id)}
//                                             />
//                                             {recurrence.id !== 1 && (
//                                                 <RiDeleteBin5Line
//                                                     className="w-8 h-8 cursor-pointer text-red-500 hover:opacity-80"
//                                                     alt="delete"
//                                                     onClick={() => handleDeleteClick(recurrence.id)} // Open delete confirmation
//                                                 />
//                                             )}
//                                             <BiSolidEdit
//                                                 onClick={() => toggleExpand(recurrence.id)}
//                                                 alt="edit"
//                                                 className="w-8 h-8 cursor-pointer text-blue-500 hover:opacity-80"
//                                             />
//                                         </div>
//                                     </div>

//                                     <div
//                                         className={`overflow-hidden transition-all mt-10 duration-1000 ease-in-out`}
//                                         style={{
//                                             height: recurrence.expanded ? 'auto' : '0', // Control height for smooth transition
//                                             opacity: recurrence.expanded ? '1' : '0', // Fade in/out
//                                             paddingTop: recurrence.expanded ? '10px' : '0', // Adjust padding to smooth transition
//                                             paddingBottom: recurrence.expanded ? '10px' : '0', // Adjust padding to smooth transition
//                                         }}
//                                     ></div>

//                                     {recurrence.expanded && (
//                                         <div className="p-4 bg-white space-y-4">
//                                             <div>
//                                                 <label className="block text-gray-700 font-medium">
//                                                     Recurrence Name
//                                                 </label>
//                                                 <input
//                                                     type="text"
//                                                     className="w-full border rounded px-3 py-2"
//                                                     value={recurrence.name}
//                                                     onChange={(e) =>
//                                                         handleEditChange(recurrence.id, "name", e.target.value)
//                                                     }
//                                                 />
//                                             </div>
//                                             <div>
//                                                 <label className="block text-gray-700 font-medium">
//                                                     Recurrence Label
//                                                 </label>
//                                                 <input
//                                                     type="text"
//                                                     className="w-full border rounded px-3 py-2"
//                                                     value={recurrence.label}
//                                                     onChange={(e) =>
//                                                         handleEditChange(recurrence.id, "label", e.target.value)
//                                                     }
//                                                 />
//                                             </div>
//                                             {recurrence.id !== 1 && (
//                                                 <div>
//                                                     <label className="block text-gray-700 font-medium">
//                                                         Recurrence Days
//                                                     </label>
//                                                     <input
//                                                         type="text"
//                                                         className="w-full border rounded px-3 py-2"
//                                                         value={recurrence.days}
//                                                         onChange={(e) =>
//                                                             handleEditChange(recurrence.id, "days", e.target.value)
//                                                         }
//                                                     />
//                                                 </div>)}
//                                             {recurrence.id !== 1 && (
//                                                 <div>
//                                                     <label className="block text-gray-700 font-medium">
//                                                         Discount Type
//                                                     </label>
//                                                     <select
//                                                         className="w-full border rounded px-3 py-2"
//                                                         value={recurrence.discountType}
//                                                         onChange={(e) =>
//                                                             handleEditChange(recurrence.id, "discountType", e.target.value)
//                                                         }
//                                                     >
//                                                         <option value="">Select Discount Type</option>
//                                                         <option value="percentage">Percentage</option>
//                                                         <option value="fixed">Flat</option>
//                                                     </select>
//                                                 </div>)}
//                                             {recurrence.id !== 1 && (
//                                                 <div>
//                                                     <label className="block text-gray-700 font-medium">
//                                                         Discount Value
//                                                     </label>
//                                                     <input
//                                                         type="text"
//                                                         className="w-full border rounded px-3 py-2"
//                                                         value={recurrence.discountValue}
//                                                         onChange={(e) =>
//                                                             handleEditChange(recurrence.id, "discountValue", e.target.value)
//                                                         }
//                                                     />
//                                                 </div>)}
//                                             <button
//                                                 className="bg-blue-500 text-white px-4 py-2 mt-4 rounded hover:bg-blue-600"
//                                                 onClick={() => updateRecurrence(recurrence.id)}
//                                             >
//                                                 Update
//                                             </button>
//                                         </div>
//                                     )}
//                                 </div>
//                             ))}
//                         </>
//                     )}</div>
//                 {/* Right-side slider */}
//                 {isSliderOpen && (
//                     <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
//                         <div className="fixed top-0 right-0 w-full sm:w-1/3 h-full bg-white shadow-lg z-50 p-6 max-w-full sm:max-w-lg md:max-w-2xl lg:max-w-4xl xl:max-w-5xl">
//                             <div className="flex items-center justify-between mb-4">
//                                 <h2 className="text-lg font-semibold">Add Recurrence</h2>
//                                 <button className="text-4xl"
//                                 onClick={() => setIsSliderOpen(false)}>
//                                     x
//                                 </button>
//                             </div>
//                             <div>
//                                 <label className="block text-gray-700 font-medium">
//                                     Recurrence Name
//                                 </label>
//                                 <input
//                                     type="text"
//                                     name="name"
//                                     className="w-full border rounded px-3 py-2"
//                                     value={newRecurrence.name}
//                                     onChange={handleInputChange}
//                                 />
//                             </div>
//                             <div>
//                                 <label className="block text-gray-700 font-medium">
//                                     Recurrence Label
//                                 </label>
//                                 <input
//                                     type="text"
//                                     name="label"
//                                     className="w-full border rounded px-3 py-2"
//                                     value={newRecurrence.label}
//                                     onChange={handleInputChange}
//                                 />
//                             </div>
//                             <div>
//                                 <label className="block text-gray-700 font-medium">
//                                     Recurrence Days
//                                 </label>
//                                 <input
//                                     type="text"
//                                     name="days"
//                                     className="w-full border rounded px-3 py-2"
//                                     value={newRecurrence.days}
//                                     onChange={handleInputChange}
//                                 />
//                             </div>
//                             <div>
//                                 <label className="block text-gray-700 font-medium">
//                                     Discount Type
//                                 </label>
//                                 <select
//                                     name="discountType"
//                                     className="w-full border rounded px-3 py-2"
//                                     value={newRecurrence.discountType}
//                                     onChange={handleInputChange}
//                                 >
//                                     <option value="">Select Discount Type</option>
//                                     <option value="percentage">Percentage</option>
//                                     <option value="fixed">Flat</option>
//                                 </select>
//                             </div>
//                             <div>
//                                 <label className="block text-gray-700 font-medium">
//                                     Discount Value
//                                 </label>
//                                 <input
//                                     type="text"
//                                     name="discountValue"
//                                     className="w-full border rounded px-3 py-2"
//                                     value={newRecurrence.discountValue}
//                                     onChange={handleInputChange}
//                                 />
//                             </div>
//                             <div className="mt-4 flex justify-end">
//                                 <button
//                                     className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
//                                     onClick={addRecurrence}
//                                 >
//                                     Add Recurrence
//                                 </button>
//                                 <button
//                                     className="ml-2 bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500"
//                                     onClick={() => setIsSliderOpen(false)} // Close slider
//                                 >
//                                     Cancel
//                                 </button>
//                             </div>
//                         </div>
//                     </div>
//                 )}

//                 {/* Delete Confirmation Modal */}
//                 {deleteConfirmation && (
//                     <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
//                         <div className="bg-white p-6 rounded-lg space-y-4">
//                             <h2 className="text-xl font-semibold">Are you sure you want to delete this recurrence?</h2>
//                             <div className="flex justify-end space-x-4">
//                                 <button
//                                     className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
//                                     onClick={deleteRecurrence}
//                                 >
//                                     Yes, Delete
//                                 </button>
//                                 <button
//                                     className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
//                                     onClick={() => setDeleteConfirmation(false)} // Close modal
//                                 >
//                                     Cancel
//                                 </button>
//                             </div>
//                         </div>
//                     </div>
//                 )}
//             </div>
//         </div>
//     );
// };

// export default Recurrence;
